import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  GitBranch, Clock, RotateCcw, GitCompare, Eye, Loader2, 
  ChevronRight, FileText, ArrowLeftRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ArchitectureVersions = ({ projectId, onClose, onRestore }) => {
  const { token } = useAuth();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareVersions, setCompareVersions] = useState({ a: null, b: null });
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    fetchVersions();
  }, [projectId]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/projects/${projectId}/arch-versions`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setVersions(response.data);
    } catch (error) {
      toast.error("Failed to load architecture versions");
    } finally {
      setLoading(false);
    }
  };

  const viewVersionDetail = async (versionNum) => {
    try {
      setDetailLoading(true);
      const response = await axios.get(
        `${API}/projects/${projectId}/arch-versions/${versionNum}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setSelectedVersion(response.data);
    } catch (error) {
      toast.error("Failed to load version details");
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleCompareVersion = (version) => {
    if (!compareVersions.a) {
      setCompareVersions({ a: version.version, b: null });
    } else if (compareVersions.a === version.version) {
      setCompareVersions({ a: null, b: null });
    } else if (!compareVersions.b) {
      setCompareVersions({ ...compareVersions, b: version.version });
    } else if (compareVersions.b === version.version) {
      setCompareVersions({ ...compareVersions, b: null });
    } else {
      setCompareVersions({ a: version.version, b: null });
    }
  };

  const runComparison = async () => {
    if (!compareVersions.a || !compareVersions.b) {
      toast.error("Select two versions to compare");
      return;
    }
    
    try {
      setComparing(true);
      const response = await axios.post(
        `${API}/projects/${projectId}/arch-versions/compare`,
        { version_a: compareVersions.a, version_b: compareVersions.b },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setCompareResult(response.data);
    } catch (error) {
      toast.error("Failed to compare versions");
    } finally {
      setComparing(false);
    }
  };

  const restoreVersion = async (versionNum) => {
    try {
      setRestoring(true);
      await axios.post(
        `${API}/projects/${projectId}/arch-versions/${versionNum}/restore`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      toast.success(`Restored to version ${versionNum}`);
      if (onRestore) onRestore();
      setSelectedVersion(null);
    } catch (error) {
      toast.error("Failed to restore version");
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleString();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <GitBranch className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Architecture Versions</h2>
            <p className="text-sm text-zinc-500">Track and compare architecture changes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={compareMode ? "default" : "outline"}
            onClick={() => {
              setCompareMode(!compareMode);
              setCompareVersions({ a: null, b: null });
              setCompareResult(null);
            }}
            className={compareMode ? "bg-[#00f0ff] text-black" : "border-zinc-700 text-zinc-300"}
            data-testid="compare-mode-btn"
          >
            <GitCompare className="w-4 h-4 mr-2" />
            Compare
          </Button>
          <Button variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">
            Close
          </Button>
        </div>
      </div>

      {compareMode && (
        <Card className="bg-[#00f0ff]/5 border-[#00f0ff]/30">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-zinc-400">
                  Version A: <span className="text-white font-bold">{compareVersions.a || "Select"}</span>
                </span>
                <ArrowLeftRight className="w-4 h-4 text-zinc-600" />
                <span className="text-zinc-400">
                  Version B: <span className="text-white font-bold">{compareVersions.b || "Select"}</span>
                </span>
              </div>
              <Button
                onClick={runComparison}
                disabled={!compareVersions.a || !compareVersions.b || comparing}
                className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                data-testid="run-compare-btn"
              >
                {comparing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Compare"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-[#00f0ff] animate-spin" />
        </div>
      ) : versions.length === 0 ? (
        <Card className="bg-[#09090b] border-white/5">
          <CardContent className="py-12 text-center">
            <GitBranch className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">No architecture versions yet</p>
            <p className="text-sm text-zinc-500">Generate your first architecture to create version 1</p>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[400px]">
          <div className="space-y-3 pr-4">
            {versions.map((version, index) => (
              <Card 
                key={version.id}
                className={`bg-[#09090b] border-white/5 hover:border-white/10 transition-colors cursor-pointer ${
                  compareMode && (compareVersions.a === version.version || compareVersions.b === version.version)
                    ? "border-[#00f0ff]/50 bg-[#00f0ff]/5"
                    : ""
                }`}
                onClick={() => compareMode ? toggleCompareVersion(version) : viewVersionDetail(version.version)}
                data-testid={`arch-version-item-${index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-sm bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                        <span className="text-xl font-bold text-blue-400">v{version.version}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">
                          {version.change_summary || `Version ${version.version}`}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-zinc-500 mt-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(version.created_at)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {compareMode ? (
                        <div className={`w-6 h-6 rounded border-2 ${
                          compareVersions.a === version.version || compareVersions.b === version.version
                            ? "bg-[#00f0ff] border-[#00f0ff]"
                            : "border-zinc-600"
                        }`} />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-zinc-600" />
                      )}
                    </div>
                  </div>
                  
                  {version.executive_summary && (
                    <p className="text-xs text-zinc-500 mt-3 line-clamp-2">
                      {version.executive_summary}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}

      {/* Comparison Result */}
      {compareResult && (
        <Card className="bg-[#09090b] border-white/5 mt-4">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-[#00f0ff]" />
              Comparison: v{compareResult.version_a.version} vs v{compareResult.version_b.version}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-sm">
                <span className="text-zinc-500">Components (v{compareResult.version_a.version}):</span>
                <span className="text-white ml-2">{compareResult.component_count_a}</span>
              </div>
              <div className="text-sm">
                <span className="text-zinc-500">Components (v{compareResult.version_b.version}):</span>
                <span className="text-white ml-2">{compareResult.component_count_b}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <span className="text-sm text-zinc-400">Changed Sections:</span>
              {compareResult.field_changes.length === 0 ? (
                <p className="text-xs text-zinc-500">No changes detected</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {compareResult.field_changes.map((change, i) => (
                    <div 
                      key={i}
                      className="flex items-center gap-2 text-xs bg-yellow-500/10 text-yellow-400 px-2 py-1 rounded"
                    >
                      <FileText className="w-3 h-3" />
                      {change.field.replace(/_/g, " ")}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Version Detail Modal */}
      <Dialog open={!!selectedVersion} onOpenChange={() => setSelectedVersion(null)}>
        <DialogContent className="bg-[#09090b] border-white/10 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#00f0ff]" />
              Architecture Version {selectedVersion?.version}
            </DialogTitle>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#00f0ff] animate-spin" />
            </div>
          ) : selectedVersion && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between text-sm text-zinc-500">
                <span>Created: {formatDate(selectedVersion.created_at)}</span>
                <span>{selectedVersion.change_summary}</span>
              </div>

              {/* Executive Summary */}
              <Card className="bg-black/30 border-white/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[#00f0ff] text-sm">Executive Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">
                    {selectedVersion.phase3_data?.executive_summary || "No summary"}
                  </p>
                </CardContent>
              </Card>

              {/* Architecture Diagram */}
              {selectedVersion.phase3_data?.architecture_diagram && (
                <Card className="bg-black/30 border-white/5">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-[#00f0ff] text-sm">Architecture Diagram</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <pre className="text-xs text-zinc-300 overflow-x-auto font-mono bg-black/50 p-4 rounded">
                      {selectedVersion.phase3_data.architecture_diagram}
                    </pre>
                  </CardContent>
                </Card>
              )}

              {/* Component Count */}
              <div className="text-sm text-zinc-400">
                Components: {selectedVersion.phase3_data?.component_specs?.length || 0}
              </div>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setSelectedVersion(null)}
              className="border-zinc-700 text-zinc-300"
            >
              Close
            </Button>
            <Button
              onClick={() => restoreVersion(selectedVersion?.version)}
              disabled={restoring}
              className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
              data-testid="restore-version-btn"
            >
              {restoring ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RotateCcw className="w-4 h-4 mr-2" />
              )}
              Restore This Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
